import moment from 'moment';
import { change, formValueSelector } from 'redux-form';
import { setDtpAdjustmentMessage } from '../../redux/modules/dateTimePickerAdjustments';
import { toMoment, toDuration } from './dateTimePickerCore';

export function flashBoundPeerInput(form, peerField) {
    if (!form || !peerField)
        return;
    if (typeof document === 'undefined')
        return;
    try {
        const selector = `.DateTimePicker[data-form="${form}"] input[name="${peerField}"]`;
        const el = document.querySelector(selector);
        if (!el)
            return;
        el.classList.remove('DateTimePicker__flash');
        void el.offsetWidth; // eslint-disable-line no-unused-expressions
        el.classList.add('DateTimePicker__flash');
        window.setTimeout(() => el.classList.remove('DateTimePicker__flash'), 900);
    } catch (_e) {
        // ignore DOM issues (tests / edge cases)
    }
}

/**
 * Adjust sibling bound fields when the user commits a value that violates
 * min/max range configuration. Called after the current field’s value is committed.
 */
export function applyPeerSync({
    dispatch,
    getState,
    form,
    withTime,
    minBoundField,
    maxBoundField,
    peerMaxRange,
    outMoment,
}) {
    const st = getState();
    const sel = formValueSelector(form);
    const rangeD = toDuration(peerMaxRange);
    const rangeHumanized = rangeD ? rangeD.humanize() : null;

    if (maxBoundField) {
        const peerRaw = sel(st, maxBoundField);
        const peerM = toMoment(peerRaw);
        let target = null;
        let kind = null;
        if (peerM && peerM.isValid()) {
            if (outMoment.isAfter(peerM)) {
                target = outMoment.clone();
                kind = 'swap_before';
            } else if (rangeD) {
                const cap = outMoment.clone().add(rangeD);
                if (peerM.isAfter(cap)) {
                    target = cap.clone();
                    kind = 'range_after';
                }
            }
        } else if (rangeD) {
            const implicitGapFloor = moment().subtract(rangeD);
            if (outMoment.isBefore(implicitGapFloor)) {
                target = outMoment.clone().add(rangeD);
                kind = 'range_after';
            }
        }
        if (target) {
            let peerNext = target.clone();
            if (withTime && peerM && peerM.isValid()) {
                const preserved = peerM.clone();
                preserved.year(target.year());
                preserved.month(target.month());
                preserved.date(target.date());
                const floor = outMoment;
                const ceil = rangeD ? outMoment.clone().add(rangeD) : null;
                if (preserved.isBefore(floor))
                    peerNext = floor.clone();
                else if (ceil && preserved.isAfter(ceil))
                    peerNext = ceil.clone();
                else
                    peerNext = preserved;
            } else if (!withTime) {
                // `maxBoundField` is the "to" / upper date; date-only `outMoment` from the
                // other field may be at start-of-day — snap the peer to end of that day.
                peerNext = target.clone().endOf('day');
            }
            dispatch(change(form, maxBoundField, peerNext.toISOString()));
            flashBoundPeerInput(form, maxBoundField);
            if (kind) {
                dispatch(setDtpAdjustmentMessage(form, maxBoundField, {
                    kind,
                    range: rangeHumanized,
                }));
            }
        }
    }

    if (minBoundField) {
        const peerRaw = sel(st, minBoundField);
        const peerM = toMoment(peerRaw);
        let target = null;
        let kind = null;
        if (peerM && peerM.isValid()) {
            if (outMoment.isBefore(peerM)) {
                target = outMoment.clone();
                kind = 'swap_after';
            } else if (rangeD) {
                const cap = outMoment.clone().subtract(rangeD);
                if (peerM.isBefore(cap)) {
                    target = cap.clone();
                    kind = 'range_before';
                }
            }
        } else if (rangeD) {
            const implicitGapCeil = moment().add(rangeD);
            if (outMoment.isAfter(implicitGapCeil)) {
                target = outMoment.clone().subtract(rangeD);
                kind = 'range_before';
            }
        }
        if (target) {
            let peerNext = target.clone();
            if (withTime && peerM && peerM.isValid()) {
                const preserved = peerM.clone();
                preserved.year(target.year());
                preserved.month(target.month());
                preserved.date(target.date());
                const ceil = outMoment;
                const floor = rangeD ? outMoment.clone().subtract(rangeD) : null;
                if (preserved.isAfter(ceil))
                    peerNext = ceil.clone();
                else if (floor && preserved.isBefore(floor))
                    peerNext = floor.clone();
                else
                    peerNext = preserved;
            } else if (!withTime) {
                // `minBoundField` is the "from" / lower date; the committed `outMoment` on
                // the end field is often end-of-day — the peer start must be start-of-day.
                peerNext = target.clone().startOf('day');
            }
            dispatch(change(form, minBoundField, peerNext.toISOString()));
            flashBoundPeerInput(form, minBoundField);
            if (kind) {
                dispatch(setDtpAdjustmentMessage(form, minBoundField, {
                    kind,
                    range: rangeHumanized,
                }));
            }
        }
    }
}
